import React, { useEffect, useRef, useState } from "react";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { ChatCircleDots, X, PaperPlaneRight } from "@phosphor-icons/react";

export default function Chatbot({ onOpenForm }) {
  const { t } = useI18n();
  const c = t.chatbot;
  const [open, setOpen] = useState(false);
  const [hubspotMode, setHubspotMode] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [stage, setStage] = useState(0); // 0..4 questions, 5 name, 6 email, 7 done
  const [answers, setAnswers] = useState([]);
  const [lead, setLead] = useState({ full_name: "", email: "" });
  const scrollRef = useRef(null);

  // Check whether HubSpot chat should be embedded instead of the custom assistant.
  useEffect(() => {
    api
      .get("/public-config")
      .then((res) => {
        const { hubspot_chat_enabled, hubspot_portal_id } = res.data;
        if (hubspot_chat_enabled && hubspot_portal_id) {
          setHubspotMode(true);
          if (!document.getElementById("hs-script-loader")) {
            const s = document.createElement("script");
            s.id = "hs-script-loader";
            s.async = true;
            s.defer = true;
            s.src = `https://js.hs-scripts.com/${hubspot_portal_id}.js`;
            document.body.appendChild(s);
          }
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "bot", text: c.welcome }, { role: "bot", text: c.q[0] }]);
    }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const pushBot = (text) => setMessages((m) => [...m, { role: "bot", text }]);
  const pushUser = (text) => setMessages((m) => [...m, { role: "user", text }]);

  const submitLead = async (full_name, email, allAnswers) => {
    try {
      await api.post("/chat/intake", {
        full_name,
        email,
        note: allAnswers.map((a, i) => `${c.q[i] || ""} ${a}`).join(" | "),
      });
    } catch (e) {
      // graceful — even if it fails, continue conversation
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text) return;
    pushUser(text);
    setInput("");

    if (stage < 5) {
      const newAnswers = [...answers, text];
      setAnswers(newAnswers);
      if (stage < 4) {
        setTimeout(() => pushBot(c.q[stage + 1]), 350);
        setStage(stage + 1);
      } else {
        // finished questions -> ask name
        setStage(5);
        setTimeout(() => pushBot(c.askEmail), 300);
        setTimeout(() => pushBot(c.namePrompt), 600);
      }
      return;
    }

    if (stage === 5) {
      setLead((l) => ({ ...l, full_name: text }));
      setStage(6);
      setTimeout(() => pushBot(c.emailPrompt), 300);
      return;
    }

    if (stage === 6) {
      const finalLead = { ...lead, email: text };
      setLead(finalLead);
      setStage(7);
      await submitLead(finalLead.full_name, finalLead.email, answers);
      // Decide outcome based on last qualification answer (Q5: would you like to preregister)
      const wantsPreregister = (answers[4] || "").toLowerCase();
      const serious = /\b(yes|sí|si|oui|да|نعم|是|sure|please)\b/.test(wantsPreregister) || wantsPreregister.length === 0;
      setTimeout(() => pushBot(c.captured), 300);
      setTimeout(() => pushBot(serious ? c.serious : c.early), 700);
      return;
    }
  };

  if (hubspotMode) {
    // HubSpot Conversations widget handles the UI.
    return null;
  }

  return (
    <>
      {!open && (
        <button
          data-testid="chatbot-toggle"
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-6 z-50 flex items-center gap-3 bg-charcoal text-ivory px-5 py-4 hover:bg-secondary transition-colors duration-300"
        >
          <ChatCircleDots size={20} weight="thin" />
          <span className="font-mono text-[0.7rem] uppercase tracking-widest">{c.open}</span>
        </button>
      )}

      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-ivory border border-charcoal flex flex-col"
        >
          <div className="flex items-center justify-between border-b border-habitus-border px-5 py-4 bg-charcoal">
            <span className="font-mono text-[0.7rem] uppercase tracking-widest text-ivory">{c.title}</span>
            <button data-testid="chatbot-close" onClick={() => setOpen(false)} className="text-ivory/70 hover:text-ivory">
              <X size={18} weight="thin" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 flex flex-col gap-3">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${m.role}`}
                className={`max-w-[85%] px-4 py-3 font-body text-sm leading-relaxed ${
                  m.role === "bot"
                    ? "self-start bg-habitus-muted text-charcoal"
                    : "self-end bg-charcoal text-ivory"
                }`}
              >
                {m.text}
              </div>
            ))}
            {stage === 7 && (
              <button
                data-testid="chatbot-open-form"
                onClick={() => {
                  setOpen(false);
                  onOpenForm?.();
                }}
                className="self-start mt-2 border border-brass text-brass-muted hover:bg-brass hover:text-ivory transition-colors duration-300 px-5 py-3 font-mono text-[0.7rem] uppercase tracking-widest"
              >
                {c.goForm}
              </button>
            )}
          </div>

          {stage < 7 && (
            <div className="border-t border-habitus-border p-3 flex items-center gap-2">
              <input
                data-testid="chatbot-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                placeholder={c.placeholder}
                className="flex-1 bg-transparent font-mono text-xs px-2 py-2 focus:outline-none placeholder:text-charcoal/30"
              />
              <button data-testid="chatbot-send" onClick={handleSend} className="text-charcoal hover:text-brass transition-colors p-2">
                <PaperPlaneRight size={18} weight="thin" />
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
